import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Plus, X, Paperclip, Download, Check, AlertCircle, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { REFRESH_EVENT } from '../lib/notificationActions';

const TITLE_MAX = 150;
const MESSAGE_MAX = 2000;
const FILE_MAX = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const ALLOWED_EXT = /\.(pdf|jpe?g|png)$/i;

function StatusBadge({ status }) {
    const isRepaid = status === 'repaid';
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${
                isRepaid
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-amber-100 text-amber-800 border border-amber-200'
            }`}
            data-testid={`submission-status-${status}`}
        >
            {isRepaid ? 'Repaid' : 'Pending'}
        </span>
    );
}

function formatDateTime(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false,
        }).replace(',', ' at');
    } catch {
        return iso;
    }
}

function humanSize(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Employer-only Submissions module rendered inside ProfileSettings.
 *
 * The parent passes an `api` instance (with baseURL + auth) and the current
 * employer `user` so we can pre-fill the email contact. This component owns
 * its own list state, listens for the global notification refresh event, and
 * scrolls itself into view when navigated to with `?section=submissions`.
 */
export default function EmployerSubmissions({ api, user }) {
    const [searchParams] = useSearchParams();
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const sectionRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/employer/submissions');
            setSubmissions(data.submissions || []);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => { load(); }, [load]);

    // Silent refresh when a notification tells us there's something new.
    useEffect(() => {
        const onRefresh = (e) => {
            const keys = e.detail?.keys || [];
            if (keys.includes('submissions')) load();
        };
        window.addEventListener(REFRESH_EVENT, onRefresh);
        return () => window.removeEventListener(REFRESH_EVENT, onRefresh);
    }, [load]);

    // If navigated here via notification (?section=submissions), scroll it in.
    useEffect(() => {
        if (searchParams.get('section') === 'submissions' && sectionRef.current) {
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [searchParams]);

    const attachmentUrl = (submissionId, attachmentId) =>
        `${api.defaults.baseURL}/submissions/${submissionId}/attachments/${attachmentId}`;

    const onSubmissionCreated = (created) => {
        setSubmissions((prev) => [created, ...prev]);
        setFormOpen(false);
        setSuccessMsg('Your submission has been received. We will review it and get back to you. You can track the status of your submission in the Submissions section of your settings.');
        setTimeout(() => setSuccessMsg(''), 8000);
    };

    return (
        <div
            id="submissions-section"
            ref={sectionRef}
            className="border border-zinc-200 p-6"
            data-testid="employer-submissions-section"
        >
            <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
                <div>
                    <h3 className="font-heading font-bold text-sm text-zinc-400 uppercase tracking-wide">Submissions</h3>
                    <p className="text-xs sm:text-sm text-zinc-600 mt-1 max-w-lg">
                        Submit a claim for costs incurred due to last-minute worker cancellations.
                    </p>
                </div>
                <Button
                    onClick={() => setFormOpen(true)}
                    className="rounded-none bg-zinc-900 text-white hover:bg-zinc-800 h-10 px-4 text-sm font-semibold"
                    data-testid="new-submission-btn"
                >
                    <Plus className="w-4 h-4 mr-1" /> New Submission
                </Button>
            </div>

            {successMsg && (
                <div className="mt-4 border border-green-200 bg-green-50 p-3 flex items-start gap-2" data-testid="submission-success">
                    <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                    <span className="text-sm text-green-700">{successMsg}</span>
                </div>
            )}

            <div className="mt-5" data-testid="submissions-list">
                {loading ? (
                    <p className="text-sm text-zinc-400">Loading...</p>
                ) : submissions.length === 0 ? (
                    <p className="text-sm text-zinc-400" data-testid="submissions-empty">
                        You haven't made any submissions yet.
                    </p>
                ) : (
                    <ul className="divide-y divide-zinc-100">
                        {submissions.map((s) => {
                            const isOpen = expandedId === s.id;
                            return (
                                <li
                                    key={s.id}
                                    className="py-3"
                                    data-testid={`submission-row-${s.id}`}
                                >
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-semibold text-zinc-900 truncate" data-testid="submission-title">
                                                    {s.title}
                                                </span>
                                                <StatusBadge status={s.status} />
                                            </div>
                                            <p className="text-[11px] text-zinc-500 mt-0.5" data-testid="submission-submitted-at">
                                                {formatDateTime(s.submitted_at)}
                                            </p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            onClick={() => setExpandedId(isOpen ? null : s.id)}
                                            className="rounded-none h-8 px-3 text-xs font-semibold"
                                            data-testid={`view-details-btn-${s.id}`}
                                        >
                                            {isOpen ? <>Hide<ChevronUp className="w-3.5 h-3.5 ml-1" /></> : <>View Details<ChevronDown className="w-3.5 h-3.5 ml-1" /></>}
                                        </Button>
                                    </div>
                                    {isOpen && (
                                        <div className="mt-3 pl-3 border-l-2 border-zinc-200 space-y-3" data-testid={`submission-details-${s.id}`}>
                                            <div>
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Message</p>
                                                <p className="text-sm text-zinc-800 whitespace-pre-wrap">{s.message}</p>
                                            </div>
                                            {s.attachments?.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Attachments</p>
                                                    <ul className="space-y-1">
                                                        {s.attachments.map((a) => (
                                                            <li key={a.id} className="flex items-center gap-2 text-sm">
                                                                <FileText className="w-4 h-4 text-zinc-400" />
                                                                <span className="truncate flex-1">{a.filename}</span>
                                                                <span className="text-[11px] text-zinc-400 shrink-0">{humanSize(a.size)}</span>
                                                                <a
                                                                    href={attachmentUrl(s.id, a.id)}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-zinc-700 hover:text-zinc-900 shrink-0"
                                                                    data-testid={`submission-attachment-${a.id}`}
                                                                >
                                                                    <Download className="w-4 h-4" />
                                                                </a>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 gap-3 text-[11px] text-zinc-600">
                                                <div>
                                                    <p className="font-bold uppercase tracking-wider text-zinc-500">Email Contact</p>
                                                    <a className="underline break-all" href={`mailto:${s.email_contact}`}>{s.email_contact}</a>
                                                </div>
                                                {s.whatsapp_contact && (
                                                    <div>
                                                        <p className="font-bold uppercase tracking-wider text-zinc-500">WhatsApp</p>
                                                        <span className="break-all">{s.whatsapp_contact}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {s.status === 'repaid' && (
                                                <p className="text-[11px] text-green-700" data-testid="submission-repaid-at">
                                                    Marked as Repaid on {formatDateTime(s.repaid_at)}.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <SubmissionFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                api={api}
                defaultEmail={user?.email || ''}
                onCreated={onSubmissionCreated}
            />

            <p
                className="mt-6 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500 italic"
                data-testid="submissions-reimbursement-note"
            >
                GulfBridge reserves the right to make reimbursements using the most cost-effective method.
            </p>
        </div>
    );
}

function SubmissionFormDialog({ open, onOpenChange, api, defaultEmail, onCreated }) {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [emailContact, setEmailContact] = useState(defaultEmail);
    const [whatsapp, setWhatsapp] = useState('');
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Reset the whole form each time the dialog opens.
    useEffect(() => {
        if (open) {
            setTitle('');
            setMessage('');
            setEmailContact(defaultEmail || '');
            setWhatsapp('');
            setFiles([]);
            setErrors({});
            setSubmitError('');
        }
    }, [open, defaultEmail]);

    const messageLeft = MESSAGE_MAX - message.length;
    const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailContact.trim()), [emailContact]);

    const onSelectFiles = (list) => {
        const rejected = [];
        const accepted = [];
        for (const f of list) {
            if (!ALLOWED_TYPES.includes(f.type) && !ALLOWED_EXT.test(f.name)) {
                rejected.push(`${f.name}: unsupported type`);
                continue;
            }
            if (f.size > FILE_MAX) {
                rejected.push(`${f.name}: exceeds 10 MB`);
                continue;
            }
            accepted.push(f);
        }
        setFiles((prev) => [...prev, ...accepted]);
        if (rejected.length > 0) {
            setErrors((e) => ({ ...e, files: rejected.join(' · ') }));
        } else {
            setErrors((e) => ({ ...e, files: undefined, attachments: undefined }));
        }
    };

    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    const validate = () => {
        const e = {};
        if (!title.trim()) e.title = 'Title is required';
        else if (title.length > TITLE_MAX) e.title = `Title must be ${TITLE_MAX} characters or fewer`;
        if (!message.trim()) e.message = 'Message is required';
        else if (message.length > MESSAGE_MAX) e.message = `Message must be ${MESSAGE_MAX} characters or fewer`;
        if (!emailContact.trim()) e.emailContact = 'Email address is required';
        else if (!emailValid) e.emailContact = 'Enter a valid email address';
        if (files.length === 0) e.attachments = 'At least one supporting document is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (evt) => {
        evt.preventDefault();
        setSubmitError('');
        if (!validate()) return;
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('title', title.trim());
            fd.append('message', message.trim());
            fd.append('email_contact', emailContact.trim());
            fd.append('whatsapp_contact', whatsapp.trim());
            for (const f of files) fd.append('files', f);
            const { data } = await api.post('/employer/submissions', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            onCreated(data.submission);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setSubmitError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again or contact support@gulfbridge.ca');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="rounded-none max-w-2xl border-2 border-zinc-900 max-h-[92vh] overflow-y-auto"
                data-testid="submission-form-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl font-black">New Submission</DialogTitle>
                    <DialogDescription className="text-sm text-zinc-600">
                        Provide the details of your claim. We'll review and get back to you.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4" noValidate>
                    <div>
                        <Label htmlFor="sub-title" className="text-sm font-semibold">Title <span className="text-red-600">*</span></Label>
                        <Input
                            id="sub-title"
                            value={title}
                            onChange={(e) => { setTitle(e.target.value.slice(0, TITLE_MAX)); setErrors((x) => ({ ...x, title: undefined })); }}
                            placeholder="e.g. Worker cancellation — visa and flight costs"
                            className={`mt-1 h-11 rounded-none ${errors.title ? 'border-red-500' : ''}`}
                            data-testid="submission-title-input"
                            maxLength={TITLE_MAX}
                        />
                        {errors.title && <p className="text-xs text-red-600 mt-1" data-testid="submission-title-error">{errors.title}</p>}
                    </div>

                    <div>
                        <Label htmlFor="sub-message" className="text-sm font-semibold">Message <span className="text-red-600">*</span></Label>
                        <Textarea
                            id="sub-message"
                            value={message}
                            onChange={(e) => { setMessage(e.target.value.slice(0, MESSAGE_MAX)); setErrors((x) => ({ ...x, message: undefined })); }}
                            placeholder="Describe the incident, the costs incurred, and any relevant details that will help us review your case."
                            className={`mt-1 rounded-none min-h-[120px] ${errors.message ? 'border-red-500' : ''}`}
                            data-testid="submission-message-input"
                            maxLength={MESSAGE_MAX}
                        />
                        <div className="flex justify-between mt-1">
                            {errors.message
                                ? <p className="text-xs text-red-600" data-testid="submission-message-error">{errors.message}</p>
                                : <span />}
                            <p className={`text-[11px] ${messageLeft < 100 ? 'text-amber-600' : 'text-zinc-400'}`} data-testid="submission-message-counter">
                                {message.length} / {MESSAGE_MAX}
                            </p>
                        </div>
                    </div>

                    <div>
                        <Label className="text-sm font-semibold">Attachments <span className="text-red-600">*</span></Label>
                        <p className="text-[11px] text-zinc-500 mt-0.5 mb-2">
                            Upload any supporting documents such as images of visa receipt, airline ticket, correspondence, etc.
                        </p>
                        <label
                            className={`flex items-center justify-center gap-2 border-2 border-dashed h-20 cursor-pointer text-sm ${errors.attachments ? 'border-red-500 text-red-600' : 'border-zinc-300 text-zinc-500 hover:border-zinc-500 hover:text-zinc-800'}`}
                            data-testid="submission-attach-dropzone"
                        >
                            <Paperclip className="w-4 h-4" />
                            <span>Click to add PDF / JPG / PNG (max 10 MB each)</span>
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                                className="hidden"
                                onChange={(e) => { onSelectFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                                data-testid="submission-attach-input"
                            />
                        </label>
                        {errors.attachments && <p className="text-xs text-red-600 mt-1" data-testid="submission-attachments-error">{errors.attachments}</p>}
                        {errors.files && <p className="text-xs text-red-600 mt-1" data-testid="submission-file-error">{errors.files}</p>}
                        {files.length > 0 && (
                            <ul className="mt-2 space-y-1" data-testid="submission-attach-list">
                                {files.map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm border border-zinc-200 px-2 py-1">
                                        <FileText className="w-3.5 h-3.5 text-zinc-400" />
                                        <span className="truncate flex-1">{f.name}</span>
                                        <span className="text-[11px] text-zinc-400 shrink-0">{humanSize(f.size)}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeFile(i)}
                                            className="text-zinc-400 hover:text-red-600 shrink-0"
                                            aria-label={`Remove ${f.name}`}
                                            data-testid={`submission-attach-remove-${i}`}
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="sub-email" className="text-sm font-semibold">Your Email Address <span className="text-red-600">*</span></Label>
                            <Input
                                id="sub-email"
                                type="email"
                                value={emailContact}
                                onChange={(e) => { setEmailContact(e.target.value); setErrors((x) => ({ ...x, emailContact: undefined })); }}
                                className={`mt-1 h-11 rounded-none ${errors.emailContact ? 'border-red-500' : ''}`}
                                data-testid="submission-email-input"
                            />
                            {errors.emailContact && <p className="text-xs text-red-600 mt-1" data-testid="submission-email-error">{errors.emailContact}</p>}
                        </div>
                        <div>
                            <Label htmlFor="sub-whatsapp" className="text-sm font-semibold">WhatsApp Number</Label>
                            <Input
                                id="sub-whatsapp"
                                value={whatsapp}
                                onChange={(e) => setWhatsapp(e.target.value)}
                                placeholder="+971501234567"
                                className="mt-1 h-11 rounded-none"
                                data-testid="submission-whatsapp-input"
                            />
                            <p className="text-[11px] text-zinc-500 mt-1">Include your country code. e.g. +971501234567</p>
                        </div>
                    </div>

                    {submitError && (
                        <div className="flex items-start gap-2 border border-red-200 bg-red-50 p-3" data-testid="submission-submit-error">
                            <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700">{submitError}</p>
                        </div>
                    )}

                    <DialogFooter className="gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="submission-cancel-btn">Cancel</Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-none bg-zinc-900 text-white hover:bg-zinc-800"
                            data-testid="submission-submit-btn"
                        >
                            {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
